import React, { useState, useEffect, useRef } from "react";
import { Container, Row, Col, Button, Nav, NavItem, NavLink, Input } from "reactstrap";
import UiContent from "../../Components/Common/UiContent";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import FlipFlashCard from "./FlipFlashCard";
import { useHistory } from "react-router-dom";
import { postDataAPI } from "../../helpers/phpbackend_helper";
import { useSelector } from "react-redux";
import CustomPagination from "../../Components/Common/CustomPagination";
import Loader from "../../Components/Common/Loader";
import AssignServersModal from "./AssignServersModal";
import DownloadFlashCardsModal from "./DownloadFlashCardsModal";

const TAB_DISHES = "Dishes";
const TAB_SCENARIOS = "Scenarios";

const FlashCards = () => {
    const history = useHistory();
    const [flashcards, setFlashcards] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pageCount, setPageCount] = useState(0);
    const [selectedPage, setSelectedPage] = useState(0);
    const [showAssignModal, setShowAssignModal] = useState(false);
    const [selectedCard, setSelectedCard] = useState(null);
    const [showDownloadModal, setShowDownloadModal] = useState(false);
    const [activeTab, setActiveTab] = useState(TAB_DISHES);
    const [searchText, setSearchText] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");

  const { currentOutlet } = useSelector((state) => ({
        currentOutlet: state.UserOutletGroup.currentOutlet
  }));

  const controllerRef = useRef(null);
  useEffect(() => {
    fetchCards();
    window.scrollTo({ top: 0, behavior: "smooth" });
    return () => {
      if (controllerRef.current) {
        controllerRef.current.abort();
      }
    };
  }, [currentOutlet, selectedPage, activeTab, debouncedSearch]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchText.trim());
    }, 600);

    return () => clearTimeout(timer);
  }, [searchText]);

  const fetchCards = () => {
    if (controllerRef.current) {
      controllerRef.current.abort();
    }

    const controller = new AbortController();
    controllerRef.current = controller;
    setLoading(true);

    const request =
      activeTab === TAB_SCENARIOS
        ? {
            serviceName: "item_data",
            op: "list_scenarios",
            load: selectedPage + 1,
            is_show_all: false,
            search: debouncedSearch
          }
        : {
            serviceName: "item_data",
            op: "server_coaching_outlet_items",
            display_classcification: "Yes",
            type: "Flash",
            status: "Active",
            load: selectedPage + 1,
            is_show_all: false,
            course_id: "",
            flash_status: "Complete",
            search: debouncedSearch
          };

    postDataAPI(request, controller.signal)
      .then((res) => {
        if (res.status === "ok") {
          setFlashcards(res.data.server_data || []);
          setPageCount(Math.ceil(parseInt(res.data.total_records) / 18));
        }
      }).catch((err) => {
        if (err.code === "ERR_CANCELED") {
          console.log("Request cancelled");
          return;
        }
        console.error(err);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleTabChange = (tab) => {
    if (tab === activeTab) return;
    setFlashcards([]);
    setActiveTab(tab);
    setSelectedPage(0);
    setSearchText("");
    setDebouncedSearch("");
  };

  const handleDeleteFlashcard = (card) => {
    const cardName = card.item_name || card.flash_card_name || "this";
    if (!window.confirm(`Delete "${cardName}" flashcard?`)) return;

    postDataAPI({
      serviceName: "item_data",
      op: "server_coaching_delete_flash_card",
      flash_id: card.flash_id
    }).then((res) => {
      if (res.status === "ok") {
        fetchCards();
      } else {
        alert(res.message || "Failed to delete flashcard");
      }
    });
  };

  const handleDownloadAll = () => {
    setShowDownloadModal(true);
  };

  const tabConfig = [
    { key: TAB_DISHES, label: "Dishes" },
    { key: TAB_SCENARIOS, label: "Scenarios" }
  ];

  return (
    <React.Fragment>
    <UiContent />
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title="Flash Cards" pageTitle="Dashboard &nbsp; > &nbsp; Server Performance Coaching" />
        <Row className="align-items-center mb-4 g-2">
          <Col lg="3" md="5">
            <Input
              type="search"
              placeholder={
                activeTab === TAB_SCENARIOS
                  ? "Search scenarios..."
                  : "Search flashcards..."
              }
              value={searchText}
              onChange={(e) => {
                setSearchText(e.target.value);
                setSelectedPage(0);
              }}
            />
          </Col>
          <Col className="text-end">
          <Button outline className="me-2"
                onClick={() => history.push("/server-performance-coaching")}
            >
                ⬅ Back to Home
            </Button>
            <Button
              color="primary"
              onClick={handleDownloadAll}
            >
              Download
            </Button>
          </Col>
        </Row>
        <Nav tabs className="mb-3">
          {tabConfig.map((tab) => (
            <NavItem key={tab.key}>
              <NavLink
                href="#"
                className={activeTab === tab.key ? "active" : ""}
                onClick={(e) => {
                  e.preventDefault();
                  handleTabChange(tab.key);
                }}
              >
                {tab.label}
              </NavLink>
            </NavItem>
          ))}
        </Nav>
        <div className="position-relative" style={{ minHeight: "300px" }}>
          {loading && <Loader />}
          <Row className={`g-4 ${loading ? "opacity-25" : ""}`}>
            {!loading && flashcards.length === 0 ? (
              <Col>
                <p className="text-center text-muted py-4">
                  {activeTab === TAB_SCENARIOS
                    ? "No scenarios found."
                    : "No flashcards found."}
                </p>
              </Col>
            ) : (
              flashcards.map(card => (
                <Col key={card.flash_id || card.item_id} sm="6" md="3" lg="4">
                  <FlipFlashCard
                    card={card}
                    isScenario={activeTab === TAB_SCENARIOS}
                    onAssign={() => {
                      setSelectedCard(card);
                      setShowAssignModal(true);
                    }}
                    refreshCards={fetchCards}
                    onDelete={() => handleDeleteFlashcard(card)}
                  />
                </Col>
              ))
            )}
            <CustomPagination
              key={`${activeTab}-${debouncedSearch}`}
              pageCount={pageCount}
              setSelectedPage={setSelectedPage}
            />
          </Row>
        </div>

        {showAssignModal && (
          <AssignServersModal
            isOpen={showAssignModal}
            toggle={() => setShowAssignModal(false)}
            card={selectedCard}
            isScenario={activeTab === TAB_SCENARIOS}
          />
        )}

        {showDownloadModal && (
          <DownloadFlashCardsModal
            isOpen={showDownloadModal}
            toggle={() => setShowDownloadModal(false)}
            currentOutlet={currentOutlet}
          />
        )}

      </Container>
      </div>
    </React.Fragment>
  );
};

export default FlashCards;
